import React, { useState, useCallback, useMemo, memo } from 'react';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  unoptimized?: boolean;
  [key: string]: unknown;
}

const AppImage = memo(function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  fill = false,
  sizes,
  onClick,
  fallbackSrc = '/assets/images/no_image.png',
  loading = 'lazy',
}: AppImageProps) {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    if (!hasError && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
    setIsLoading(false);
  }, [hasError, imageSrc, fallbackSrc]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const imageClassName = useMemo(() => {
    const classes = [className];
    if (priority) {
      classes.push('transition-opacity duration-200');
      const hasCustomOpacity = /opacity-\d+/.test(className);
      if (!hasCustomOpacity) classes.push(isLoading ? 'opacity-0' : 'opacity-100');
      else if (isLoading) classes.push('opacity-0');
    } else if (isLoading) {
      classes.push('bg-gray-200');
    }
    if (onClick) classes.push('cursor-pointer hover:opacity-90 transition-opacity duration-200');
    return classes.filter(Boolean).join(' ');
  }, [className, isLoading, onClick, priority]);

  const imgProps = {
    src: imageSrc,
    alt,
    className: imageClassName,
    loading: priority ? 'eager' : loading,
    onError: handleError,
    onLoad: handleLoad,
    onClick,
    ...(width != null && { width }),
    ...(height != null && { height }),
    ...(sizes && { sizes }),
  };

  if (fill) {
    const hasObjectClass = /\bobject-(cover|contain|fill|none|scale-down)\b/.test(imageClassName);
    return (
      <div className="relative w-full h-full">
        <img
          {...imgProps}
          className={`${imageClassName} w-full h-full ${hasObjectClass ? '' : 'object-cover'}`}
        />
      </div>
    );
  }

  /** Sem `width`/`height` em `imgProps`: layout só por CSS (ex.: `h-*` + `w-auto` na logo). */
  return <img {...imgProps} />;
});

AppImage.displayName = 'AppImage';

export default AppImage;
